import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'

import ChannelGuard from '@/guards/ChannelGuard'
import AssembleLayout from '@/layouts/AssembleLayout'
import MainLayout from '@/layouts/MainLayout'
import { useFetch } from '@/lib/hooks'
import {
  channelMiddleware,
  showcaseMiddleware,
  siteMiddleware,
  storeMiddleware
} from '@/lib/middlewares'
import { urlHostResolver } from '@/lib/utils'
import { noIndex } from '@/lib/utils/next-seo.config'
import {
  Channel as ChannelType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'
import ShowcaseView from '@/views/sites/ShowcaseView'

interface Data {
  site?: SiteType
  channel?: ChannelType
  store?: StoreType
  showcase?: ShowcaseType
}

interface Props {
  initialData?: Data
}

export default function ShowcasePage({ initialData }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const {
    site: siteSlug,
    channel: channelSlug,
    store: storeSlug,
    showcase: showcaseSlug
  } = router?.query || {}

  const { site: siteData, channel: channelData } = initialData || {}

  // Fetch
  const { data: showcaseData } = useFetch(
    [
      siteSlug && channelSlug && storeSlug && showcaseSlug
        ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/showcases/${showcaseSlug}/`
        : null
    ],
    { fallbackData: initialData?.showcase } // rehydration if necessary
  )

  // SEO
  const path = `/${channelSlug}/${storeSlug}/${showcaseSlug}`
  const urlSEO = siteData?.domain
    ? `${siteData.domain}${path}`
    : urlHostResolver(siteData?.subdomain, path)

  const commonSEOProps = {
    title: `${showcaseData?.displayName || 'Vitrine'} ${showcaseData?.cycle || ''}${
      channelData ? ` | ${channelData?.title}` : ''
    }`,
    description: `Veja os produtos da vitrine ${showcaseData?.displayName}`
  }

  return (
    <AssembleLayout
      Layout={MainLayout}
      Guard={ChannelGuard}
      View={ShowcaseView}
      initialData={{ initialData }}>
      <NextSeo
        canonical={urlSEO}
        {...commonSEOProps}
        openGraph={{
          ...commonSEOProps,
          url: urlSEO,
          images: [
            {
              url: (showcaseData?.image || channelData?.cover) as string,
              alt: commonSEOProps.title
            }
          ]
        }}
        {...(channelData?.privacy !== 'public' ? noIndex : {})}
      />
    </AssembleLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async context => {
  const { data: siteData } = await siteMiddleware(context)
  const { data: channelData } = await channelMiddleware(context)
  const { data: storeData } = await storeMiddleware(context)
  const { data: showcaseData, error: showcaseDataError } = await showcaseMiddleware(context)

  return showcaseDataError?.response?.status === 404
    ? { notFound: true, revalidate: 60 }
    : {
        props: {
          initialData: {
            site: siteData,
            channel: channelData,
            store: storeData,
            showcase: showcaseData
          }
        },
        revalidate: 60
      }
} // revalidate time in seconds
